// ClubLinkCard — the chat identity card for a TransferPitch club / agency
// profile link (parity #18). The sibling of ProfileCard (player/coach): it
// unfurls a /search/club/:id or /search/agency/:id URL into a compact card in
// the style of the player/coach previews — crest/avatar + name + a quiet meta
// line + an "Open in TransferPitch" CTA — instead of a raw URL and a generic OG
// banner. Self-healing like ProfileCard: on a failed/empty fetch it degrades to
// a plain clickable link, so hiding the text URL can never leave an empty bubble.

import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ClubCrest from "../common/ClubCrest";
import Avatar from "../common/Avatar";
import { clubService } from "../../services/api";

import "./ProfileCard.css";

const routeFor = (type, id) =>
  type === "agency" ? `/search/agency/${id}` : `/search/club/${id}`;

export default function ClubLinkCard({ type, id, fullUrl }) {
  const navigate = useNavigate();
  const [status, setStatus] = useState("loading"); // loading | ready | error
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setStatus("loading");
    const fetcher =
      type === "agency"
        ? clubService.getAgencyPublicProfile
        : clubService.getClubPublicProfile;
    Promise.resolve(fetcher(id))
      .then((res) => {
        if (cancelled) return;
        if (res?.success && res.data) {
          setData(res.data);
          setStatus("ready");
        } else {
          setStatus("error");
        }
      })
      .catch(() => {
        if (!cancelled) setStatus("error");
      });
    return () => {
      cancelled = true;
    };
  }, [type, id]);

  if (status === "loading") {
    return (
      <div className="tp-card-chat">
        <div className="tp-card-skeleton" aria-label="Loading profile preview" />
      </div>
    );
  }

  // Normalise the two payload shapes. Club: { club:{name,image_url,country,
  // city}, competition:{name} }. Agency: a flat object; probe the common field
  // names without inventing anything the payload doesn't carry.
  const club = data?.club || null;
  const name =
    type === "agency"
      ? data?.name || data?.agency_name || data?.title || null
      : club?.name || data?.name || null;
  const logoUrl =
    type === "agency"
      ? data?.logo || data?.logo_url || data?.image_url || data?.image || null
      : club?.image_url || club?.logo || null;
  const metaLine =
    type === "agency"
      ? [data?.country, data?.city].filter(Boolean).join(" · ")
      : [club?.country, data?.competition?.name].filter(Boolean).join(" · ");

  // Error, or a payload with nothing to show → self-heal to a plain link so the
  // message is never left empty by the no-raw-links strip.
  if (status === "error" || !name) {
    return (
      <div className="tp-card-chat">
        <div className="tp-card-error">
          This profile is no longer available.
          {fullUrl && (
            <>
              {" "}
              <a href={fullUrl} target="_blank" rel="noopener noreferrer">
                {fullUrl}
              </a>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="tp-card-chat">
      <div
        className="flex items-center gap-3 rounded-[12px] border border-[#E4E9F1] bg-white"
        style={{ padding: "12px 14px", maxWidth: 340 }}
      >
        {type === "agency" ? (
          <Avatar src={logoUrl} alt={name} fallbackText={name} size={40} />
        ) : (
          <ClubCrest name={name} logoUrl={logoUrl} variant="card" />
        )}
        <div className="flex min-w-0 flex-1 flex-col" style={{ gap: 2 }}>
          <span className="truncate text-[14px] font-semibold text-[#14263D]" title={name}>
            {name}
          </span>
          <span className="text-[11px] font-medium text-[#98A5B8]">
            {type === "agency" ? "Agency" : "Club"}
            {metaLine ? ` · ${metaLine}` : ""}
          </span>
          <button
            type="button"
            onClick={() => navigate(routeFor(type, id))}
            className="mt-1 inline-flex w-fit items-center gap-1 text-[11.5px] font-semibold text-[#1466D8]"
          >
            Open in TransferPitch
            <svg width="11" height="11" viewBox="0 0 12 12" fill="none">
              <path
                d="M2 6h8M7 3l3 3-3 3"
                stroke="#1466D8"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}
